<template>
  <component
    :is="element"
    class="product-prices"
    :class="[
      $blockClass(blockClass, 'prices'),
      { 'product-prices--reverse': reverse },
    ]"
  >
    <span
      v-if="formattedCompareAtPrice && !reverse"
      class="product-prices__compare-at-price"
      :class="$blockClass(blockClass, 'compare-at-price')"
      v-text="formattedCompareAtPrice"
    />

    <span
      class="product-prices__price"
      :class="[
        $blockClass(blockClass, 'price'),
        { 'product-prices__price--on-sale': formattedCompareAtPrice },
      ]"
      js-product="price"
      v-text="formattedPrice"
    />

    <span
      v-if="formattedCompareAtPrice && reverse"
      class="product-prices__compare-at-price"
      :class="$blockClass(blockClass, 'compare-at-price')"
      v-text="formattedCompareAtPrice"
    />

    <span
      v-if="discountPercentage"
      class="product-prices__discount"
      :class="$blockClass(blockClass, 'discount')"
      v-text="`${$string('product.prices.discount',{ replace: { discount: discountPercentage } })}`"
    />
  </component>
</template>

<script>

/**
 * Vue: Product prices
 * -----------------------------------------------------------------------------
 * Global product prices template.
 *
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {Number|Boolean} compareAtPrice - Compare at price if it exists.
 * @param {String} [element] - Custom element override.
 * @param {Boolean} [is_gift_card] - Product is gift card.
 * @param {Number} price - Product price, in cents format.
 * @param {Boolean} [reverse] - Reverse order of prices.
 *
 */
export default {
  name: 'ProductPrices',

  props: {
    blockClass: {
      type: String,
      default: '',
    },
    compareAtPrice: {
      type: [Boolean, Number],
      default: false,
      required: true,
    },
    element: {
      type: String,
      default: 'p',
    },
    isGiftCard: {
      type: Boolean,
      default: false,
    },
    price: {
      type: [Boolean, Number],
      default: 0,
      required: true,
    },
    reverse: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      cnvsGlobal: true,
    }
  },

  computed: {

    /**
     * Compute compare at price template.
     * @returns {String}
     */
    formattedCompareAtPrice() {
      return this.compareAtPrice && this.price < this.compareAtPrice
        ? this.$formatMoney(this.compareAtPrice).replace('.00', '')
        : ''
    },

    /**
     * Compute price template.
     * @returns {String}
     */
    formattedPrice() {
      if (this.price === false) {
        return this.$string('accessibility.loading')
      }

      if (this.price <= 0) {
        return this.$string('product.prices.free')
      }

      if (this.isGiftCard) {
        const price = Math.round(this.price / 100) * 100
        return this.$formatMoney(price).replace('.00', '')
      }

      return this.price ? this.$formatMoney(this.price).replace('.00', '') : ''
    },

    /**
    * Compute discount percentage.
    * @returns {Number}
    */
    discountPercentage() {
      if (this.compareAtPrice && this.price < this.compareAtPrice) {
        return Math.round(((this.compareAtPrice - this.price) / this.compareAtPrice) * 100)
      }

      return 0
    },
  },
}
</script>
